import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdAssignmentReturn,
  MdCurrencyRupee,
  MdHourglassTop,
  MdCheckCircle,
  MdPercent,
  MdTimer,
  MdCancel,
  MdVideocam,
  MdLocalShipping,
  MdPictureAsPdf,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/returns';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
const label = (s) => (s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;

const RETURN_STATUS = {
  requested: ['#fef3c7', '#92400e'],
  approved: ['#e0e7ff', '#3730a3'],
  pickedup: ['#e0f2fe', '#075985'],
  received: ['#ccfbf1', '#115e59'],
  refund_completed: ['#dcfce7', '#166534'],
  rejected: ['#fee2e2', '#991b1b'],
};
const REFUND_STATUS = {
  not_initiated: ['#f1f5f9', '#475569'],
  processing: ['#fef3c7', '#92400e'],
  refunded: ['#dcfce7', '#166534'],
};
const CONDITION = { good: ['#dcfce7', '#166534'], damaged: ['#fee2e2', '#991b1b'], expired: ['#ffedd5', '#9a3412'] };
const chip = (map, v, text) => {
  const [bg, fg] = map[v] || ['#f1f5f9', '#475569'];
  return <Chip size="small" label={text || label(v)} sx={pill(bg, fg)} />;
};
const cancelRefundChip = (s) => {
  const done = /refund|complete|success|done/i.test(s) && !/pending|process|initiat/i.test(s);
  return <Chip size="small" label={label(s)} sx={done ? pill('#dcfce7', '#166534') : pill('#fef3c7', '#92400e')} />;
};

const ProductCell = ({ r, name = r.product }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 200 }}>
    <Avatar variant="rounded" src={r.image || undefined} alt={name} sx={{ width: 36, height: 36, bgcolor: '#e2e8f0', color: '#475569', fontWeight: 700, fontSize: 12.5 }}>
      {(name || '?').charAt(0).toUpperCase()}
    </Avatar>
    <Box sx={{ lineHeight: 1.25, minWidth: 0 }}>
      <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</Typography>
      <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{[r.brand, r.productCode].filter(Boolean).join(' · ') || '—'}</Typography>
    </Box>
  </Box>
);

const PersonCell = ({ name, phone }) => (
  <Box sx={{ lineHeight: 1.25 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 600, fontFamily: FONT }}>{name}</Typography>
    {phone && <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{phone}</Typography>}
  </Box>
);

const col = (key, lbl, opts = {}) => ({ key, label: lbl, ...opts });
const money = (key, lbl) => col(key, lbl, { numeric: true, bold: true, text: (r) => r[key], cell: (r) => inr(r[key]), print: (r) => inr(r[key]) });

const TABS = [
  {
    key: 'returns',
    label: 'Return Requests',
    sorts: [
      ['date_desc', 'Newest first'],
      ['date_asc', 'Oldest first'],
      ['amount_desc', 'Refund value: high to low'],
      ['age_desc', 'Oldest open first'],
    ],
    columns: [
      col('returnId', 'Return ID', { bold: true, text: (r) => r.returnId }),
      col('requestedAt', 'Requested', { text: (r) => fmtDate(r.requestedAt) }),
      col('orderNumber', 'Order No.', { text: (r) => r.orderNumber }),
      col('customer', 'Customer', { text: (r) => r.customer, cell: (r) => <PersonCell name={r.customer} phone={r.phone} />, extra: (r) => ({ Phone: r.phone }) }),
      col('product', 'Product', { text: (r) => r.product, cell: (r) => <ProductCell r={r} />, extra: (r) => ({ 'Product Code': r.productCode }) }),
      col('quantity', 'Qty', { center: true, text: (r) => r.quantity }),
      col('reason', 'Reason', { text: (r) => label(r.reason) }),
      col('condition', 'Condition', { text: (r) => label(r.condition), cell: (r) => chip(CONDITION, r.condition) }),
      money('refundAmount', 'Refund Value'),
      col('returnStatus', 'Return Status', { text: (r) => label(r.returnStatus), cell: (r) => chip(RETURN_STATUS, r.returnStatus) }),
      col('refundStatus', 'Refund Status', { text: (r) => label(r.refundStatus), cell: (r) => chip(REFUND_STATUS, r.refundStatus) }),
      col('age', 'Age / TAT', {
        center: true,
        text: (r) => (r.ageDays !== null ? `Open ${r.ageDays}d` : r.resolutionDays !== null ? `Closed in ${r.resolutionDays}d` : ''),
        cell: (r) =>
          r.ageDays !== null ? (
            <Chip size="small" label={`Open ${r.ageDays}d`} sx={r.ageDays > 7 ? pill('#fee2e2', '#b91c1c') : pill('#fef3c7', '#92400e')} />
          ) : r.resolutionDays !== null ? (
            <Typography sx={{ fontSize: 11.5, color: '#475569', fontFamily: FONT }}>{r.resolutionDays}d</Typography>
          ) : (
            dash
          ),
      }),
      col('links', 'Links', {
        center: true,
        text: (r) => r.awb || '',
        extra: (r) => ({ 'Return Type': label(r.returnType), 'Refunded On': r.refundedAt ? fmtDate(r.refundedAt) : '' }),
        cell: (r) => (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
            {r.video && (
              <Tooltip title="Return video" arrow>
                <IconButton size="small" component="a" href={r.video} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} sx={{ color: '#7c3aed' }}><MdVideocam size={18} /></IconButton>
              </Tooltip>
            )}
            {(r.trackingUrl || r.awb) && (
              <Tooltip title={r.awb ? `AWB ${r.awb}` : 'Track'} arrow>
                <IconButton size="small" component={r.trackingUrl ? 'a' : 'span'} href={r.trackingUrl || undefined} target="_blank" rel="noopener noreferrer" sx={{ color: '#0284c7' }}><MdLocalShipping size={18} /></IconButton>
              </Tooltip>
            )}
            {r.pdfUrl && (
              <Tooltip title="Return document" arrow>
                <IconButton size="small" component="a" href={r.pdfUrl} target="_blank" rel="noopener noreferrer" sx={{ color: '#dc2626' }}><MdPictureAsPdf size={18} /></IconButton>
              </Tooltip>
            )}
            {!r.video && !r.awb && !r.trackingUrl && !r.pdfUrl && dash}
          </Box>
        ),
      }),
    ],
  },
  {
    key: 'reasons',
    label: 'By Reason',
    columns: [
      col('reason', 'Reason', { bold: true, text: (r) => label(r.reason) }),
      col('requests', 'Requests', { numeric: true, text: (r) => r.requests }),
      col('sharePct', '% of Returns', {
        text: (r) => `${r.sharePct}%`,
        cell: (r) => (
          <Box sx={{ minWidth: 120, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress variant="determinate" value={Math.min(r.sharePct, 100)} sx={{ flex: 1, height: 8, borderRadius: 4, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg,#f43f5e,#dc2626)', borderRadius: 4 } }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, fontFamily: FONT, minWidth: 44, textAlign: 'right' }}>{r.sharePct}%</Typography>
          </Box>
        ),
      }),
      col('units', 'Units', { numeric: true, text: (r) => r.units }),
      money('value', 'Refund Value'),
      col('damagedCondition', 'Received Damaged / Expired', { numeric: true, text: (r) => r.damagedCondition }),
      col('rejected', 'Rejected', { numeric: true, text: (r) => r.rejected }),
    ],
  },
  {
    key: 'products',
    label: 'By Product',
    columns: [
      col('rank', '#', { center: true, text: (r, i) => i, skipExport: true }),
      col('product', 'Product', { text: (r) => r.product, cell: (r) => <ProductCell r={r} />, extra: (r) => ({ 'Product Code': r.productCode, Brand: r.brand }) }),
      col('requests', 'Requests', { numeric: true, text: (r) => r.requests }),
      col('units', 'Units Returned', { numeric: true, bold: true, text: (r) => r.units }),
      money('value', 'Refund Value'),
      col('topReason', 'Top Reason', { text: (r) => label(r.topReason) }),
    ],
  },
  {
    key: 'cancellations',
    label: 'Cancellation Refunds',
    columns: [
      col('orderNumber', 'Order No.', { bold: true, text: (r) => r.orderNumber }),
      col('cancelledAt', 'Cancelled On', { text: (r) => fmtDate(r.cancelledAt) }),
      col('customer', 'Customer', { text: (r) => r.customer, cell: (r) => <PersonCell name={r.customer} phone={r.phone} />, extra: (r) => ({ Phone: r.phone }) }),
      col('cancelledBy', 'Cancelled By', { text: (r) => label(r.cancelledBy) }),
      col('cancelReason', 'Reason', { text: (r) => r.cancelReason }),
      money('refundAmount', 'Refund Amount'),
      col('refundMode', 'Refund Mode', { text: (r) => label(r.refundMode) }),
      col('refundStatus', 'Refund Status', { text: (r) => label(r.refundStatus), cell: (r) => cancelRefundChip(r.refundStatus) }),
    ],
  },
];

const ReturnReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];

  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [refundStatus, setRefundStatus] = useState('');
  const [reason, setReason] = useState('');
  const [condition, setCondition] = useState('');
  const [type, setType] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (status) p.status = status;
    if (refundStatus) p.refundStatus = refundStatus;
    if (reason) p.reason = reason;
    if (condition) p.condition = condition;
    if (type) p.type = type;
    return p;
  }, [tab.key, sortBy, from, to, search, status, refundStatus, reason, condition, type]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (i) => {
    setTabIndex(i);
    setPage(0);
  };

  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };
  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };
  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const quickStatus = (value, refund = '') => {
    goToTab(0);
    setStatus(value);
    setRefundStatus(refund);
  };

  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setStatus('');
    setRefundStatus('');
    setReason('');
    setCondition('');
    setType('');
    setSortBy('date_desc');
    setPage(0);
  };

  const exportColumns = tab.columns.filter((c) => !c.skipExport);
  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of exportColumns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Returns_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      const cols = exportColumns.filter((c) => c.key !== 'links');
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Return & Refund Report — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} rows`,
          headers: cols.map((c) => c.label),
          cells: rows.map((r, i) => cols.map((c) => ({ value: c.print ? c.print(r) : c.text(r, i + 1), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const baseIndex = page * rowsPerPage;
  const showReturnFilters = tab.key !== 'cancellations';

  const cards = [
    { label: 'Return Requests', value: (stats.requests || 0).toLocaleString('en-IN'), icon: MdAssignmentReturn, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${stats.units || 0} units · ${inr(stats.returnValue)}`, onClick: () => quickStatus(''), active: tab.key === 'returns' && !status && !refundStatus },
    { label: 'Refunded', value: inr(stats.refundedValue), icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: `${stats.refundedCount || 0} returns`, onClick: () => quickStatus('', 'refunded'), active: refundStatus === 'refunded' },
    { label: 'Pending Refunds', value: inr(stats.pendingRefundValue), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: `${stats.pendingRefundCount || 0} returns awaiting refund` },
    { label: 'Open Returns', value: (stats.open || 0).toLocaleString('en-IN'), icon: MdHourglassTop, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: `${stats.overdue || 0} older than 7 days`, onClick: () => quickStatus('open'), active: status === 'open' },
    { label: 'Return Rate', value: `${stats.returnRate || 0}%`, icon: MdPercent, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: 'Of delivered orders' },
    { label: 'Avg Resolution', value: stats.avgResolutionDays === null || stats.avgResolutionDays === undefined ? '—' : `${stats.avgResolutionDays} days`, icon: MdTimer, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: `${stats.rejected || 0} rejected`, onClick: () => quickStatus('rejected'), active: status === 'rejected' },
    { label: 'Cancellation Refunds', value: inr(stats.cancellationRefundValue), icon: MdCancel, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: `${stats.cancellationRefunds || 0} orders · ${inr(stats.cancellationPendingValue)} pending`, onClick: () => goToTab(3), active: tab.key === 'cancellations' },
  ];

  const activeFilterCount = [from || to, search, status, refundStatus, reason, condition, type, sortBy !== 'date_desc'].filter(Boolean).length;
  const maxStatus = Math.max(1, ...(stats.byStatus || []).map((s) => s.count));

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Return &amp; Refund Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>Track every return, its refund, and money owed on cancelled orders</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      {/* Status pipeline */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a', mb: 1.5 }}>Return pipeline</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 1.5 }}>
          {(stats.byStatus || []).map((s) => {
            const [bg, fg] = RETURN_STATUS[s.status];
            return (
              <Box
                key={s.status}
                onClick={() => quickStatus(status === s.status ? '' : s.status)}
                sx={{ p: 1.5, borderRadius: '12px', background: bg, cursor: 'pointer', outline: status === s.status ? `2px solid ${fg}` : 'none', transition: 'transform .15s', '&:hover': { transform: 'translateY(-2px)' } }}
              >
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: fg, fontFamily: FONT }}>{label(s.status)}</Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 800, color: fg, fontFamily: FONT }}>{s.count}</Typography>
                <LinearProgress variant="determinate" value={(s.count / maxStatus) * 100} sx={{ mt: 0.5, height: 5, borderRadius: 3, background: 'rgba(255,255,255,.6)', '& .MuiLinearProgress-bar': { background: fg } }} />
              </Box>
            );
          })}
        </Box>
      </Paper>

      {/* Filters */}
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label={tab.key === 'cancellations' ? 'Search order no. / customer / phone' : 'Search return ID / order / customer / product'}
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          {showReturnFilters && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={2.5}>
                <TextField select label="Return status" size="small" fullWidth value={status} onChange={setFilter(setStatus)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="open">All open</MenuItem>
                  {Object.keys(RETURN_STATUS).map((s) => (
                    <MenuItem key={s} value={s}>{label(s)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2.5}>
                <TextField select label="Refund status" size="small" fullWidth value={refundStatus} onChange={setFilter(setRefundStatus)}>
                  <MenuItem value="">All</MenuItem>
                  {Object.keys(REFUND_STATUS).map((s) => (
                    <MenuItem key={s} value={s}>{label(s)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField select label="Reason" size="small" fullWidth value={reason} onChange={setFilter(setReason)}>
                  <MenuItem value="">All</MenuItem>
                  {['damaged', 'wrong_item', 'quality_issue', 'customer_return', 'expired', 'other'].map((s) => (
                    <MenuItem key={s} value={s}>{label(s)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField select label="Condition" size="small" fullWidth value={condition} onChange={setFilter(setCondition)}>
                  <MenuItem value="">All</MenuItem>
                  {Object.keys(CONDITION).map((s) => (
                    <MenuItem key={s} value={s}>{label(s)}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2.5}>
                <TextField select label="Return type" size="small" fullWidth value={type} onChange={setFilter(setType)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="customer_to_warehouse">Customer → Warehouse</MenuItem>
                  <MenuItem value="customer_to_seller">Customer → Seller</MenuItem>
                  <MenuItem value="warehouse_to_seller">Warehouse → Seller</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
          {tab.sorts && (
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
                {tab.sorts.map(([v, l]) => (
                  <MenuItem key={v} value={v}>{l}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Tabs + table */}
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} rows</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>Nothing matches the selected filters.</TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow
                  key={`${r.returnId || r.orderNumber || r.reason || r.product}-${idx}`}
                  hover
                  sx={{ '&:nth-of-type(even)': { background: '#f8fafc' }, boxShadow: r.ageDays > 7 ? 'inset 4px 0 0 #dc2626' : 'none' }}
                >
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r, baseIndex + idx + 1) : c.text(r, baseIndex + idx + 1)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReturnReport;
